// 实物卡面的取图工具。
//
// 注意事项：卡面由 scripts/build_card_assets.py 从实体卡扫描件转成
// public/cards/<id>.webp（id 与 core 的 content 表一致）；查不到时返回 null，
// 调用方必须回退成文字渲染，不能让缺图破坏界面。
import type { ReviewedLine } from "./reviewed-copy.ts";


export function cardImage(id: string | null | undefined): string | null {
  if (!id) return null;
  if (!/^[a-z0-9_-]+$/i.test(id)) return null;
  return `/cards/${id}.webp`;
}

/** roleImage 与其他实体牌一样使用完整扫描卡面。 */
export function roleImage(id: string | null | undefined): string | null {
  return cardImage(id);
}

/** sceneImage 取场地插画；场地卡以完整主体及同图模糊补边铺满卡面。 */
export function sceneImage(place: string | null | undefined): string | null {
  if (!place) return null;
  if (!/^[a-z_]+$/.test(place)) return null;
  return `/cards/scene_${place}.webp`;
}

export interface TipContent {
  title: string;
  kicker?: string | undefined;
  summary?: string | undefined;
  detail?: string | undefined;
  meta?: string | undefined;
  image?: string | undefined;
  color?: string | undefined;
  layout?: "card" | undefined;
  detailLabel?: string | undefined;
  metaLabel?: string | undefined;
  titleRuns?: ReviewedLine | undefined;
  reviewLines?: readonly ReviewedLine[] | undefined;
}

type TipAttributes = Record<`data-tip-${string}`, string>;

/**
 * tip 把简短旧文案与结构化说明统一成单一 document 级悬停面板所需的 data 属性。
 */
export function tip(value: string | TipContent | undefined): TipAttributes {
  if (!value) return {};
  if (typeof value === "string") {
    const divider = value.indexOf("｜");
    const newline = value.indexOf("\n");
    const splitAt = divider >= 0 ? divider : newline;
    return splitAt >= 0
      ? {
          "data-tip-title": value.slice(0, splitAt),
          "data-tip-summary": value.slice(splitAt + 1).trim(),
        }
      : { "data-tip-title": value };
  }
  return {
    "data-tip-title": value.title,
    ...(value.kicker ? { "data-tip-kicker": value.kicker } : {}),
    ...(value.summary ? { "data-tip-summary": value.summary } : {}),
    ...(value.detail ? { "data-tip-detail": value.detail } : {}),
    ...(value.meta ? { "data-tip-meta": value.meta } : {}),
    ...(value.image ? { "data-tip-image": value.image } : {}),
    ...(value.color ? { "data-tip-color": value.color } : {}),
    ...(value.layout ? { "data-tip-layout": value.layout } : {}),
    ...(value.detailLabel ? { "data-tip-detail-label": value.detailLabel } : {}),
    ...(value.metaLabel ? { "data-tip-meta-label": value.metaLabel } : {}),
    ...(value.titleRuns ? { "data-tip-title-runs": JSON.stringify(value.titleRuns) } : {}),
    ...(value.reviewLines ? { "data-tip-review-lines": JSON.stringify(value.reviewLines) } : {}),
  };
}

/** ReviewedText 保留审核页逐段标注的条件色、数值色与自定义色。 */
export function ReviewedText({
  lines,
  className,
}: {
  lines: readonly ReviewedLine[];
  className?: string | undefined;
}) {
  return (
    <span className={["reviewed-text", className].filter(Boolean).join(" ")}>
      {lines.map((line, lineIndex) => (
        <span className="reviewed-line" key={lineIndex}>
          {line.map((run, runIndex) => (
            <span
              className={`reviewed-run tone-${run.tone}`}
              key={runIndex}
              style={run.tone === "custom" ? { color: run.color } : undefined}
            >
              {run.text}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}

/** 缺失卡面时隐藏损坏的图像节点；内容仍由相邻文字与无障碍标签表达。 */
export function cardFallback(event: React.SyntheticEvent<HTMLImageElement>): void {
  event.currentTarget.hidden = true;
}

/** CardFace 渲染一张真实卡面；缺图时渲染 children 作为回退。 */
export function CardFace({
  id,
  alt,
  className,
  children,
}: {
  id: string | null | undefined;
  alt: string;
  className?: string | undefined;
  children?: React.ReactNode;
}) {
  const source = roleImage(id);
  if (!source) return <>{children ?? null}</>;
  return (
    <img
      className={["card-face", className].filter(Boolean).join(" ")}
      src={source}
      alt={alt}
      onError={cardFallback}
      draggable={false}
      loading="lazy"
      decoding="async"
    />
  );
}


/** CardBack 是牌堆、抽牌与拖放共用的实体卡背，不泄露牌面。 */
export function CardBack({
  deck,
  className,
}: {
  deck?: string | undefined;
  className?: string | undefined;
}) {
  const safeDeck = deck === "hakurei" || deck === "moriya" || deck === "kleinji" ? deck : "neutral";
  return (
    <span
      className={["card-back-art", `deck-back-${safeDeck}`, className].filter(Boolean).join(" ")}
      aria-hidden="true"
    >
      <img className="card-back-image" src={`/materials/card-back-${safeDeck}.webp`} alt="" draggable={false} width={1120} height={1612} />
    </span>
  );
}
